import { Button } from '@bancobarigui/barihaus';
import { LeftOutlined, RightOutlined, EyeOutlined, CheckOutlined, SaveOutlined } from '@ant-design/icons';
import { BlobProvider } from '@react-pdf/renderer';
import { LaudoStep, WizardMode } from '../../types/laudo-wizard.types';
import { LaudoDocument } from '../../pdf/LaudoDocument';
import { styles } from './styles';

interface WizardNavigationProps {
  currentStep: LaudoStep;
  canGoNext: boolean;
  canGoPrevious: boolean;
  pdfGenerated: boolean;
  processedLaudo?: any;
  logoBase64?: string;
  streetViewImageBase64?: string;
  processingUrls?: boolean;
  isTransmitting?: boolean;
  mode?: WizardMode;
  onNext: () => void;
  onPrevious: () => void;
  onFinish: () => void;
  onSaveDraft?: () => void;
  savingDraft?: boolean;
}

export function WizardNavigation({
  currentStep,
  canGoNext,
  canGoPrevious,
  pdfGenerated,
  processedLaudo,
  logoBase64,
  streetViewImageBase64,
  processingUrls,
  isTransmitting,
  mode = 'create',
  onNext,
  onPrevious,
  onFinish,
  onSaveDraft,
  savingDraft,
}: WizardNavigationProps) {
  const isLastStep = currentStep === LaudoStep.TRANSMISSAO;
  const isReviewMode = mode === 'review';
  const isEditAutomaticMode = mode === 'editAutomatic';
  const isCalculationsMode = mode === 'calculations';

  const showSaveDraft = onSaveDraft && !isLastStep && !isReviewMode && !isEditAutomaticMode;

  const getFinishButtonText = () => {
    if (isTransmitting) {
      if (isEditAutomaticMode) return 'Salvando...';
      if (isCalculationsMode) return 'Transmitindo Cálculos...';
      return 'Transmitindo...';
    }
    if (isEditAutomaticMode) return 'Salvar';
    if (isCalculationsMode) return 'Transmitir Cálculos';
    return 'Transmitir';
  };

  return (
    <div style={styles.navigationContainer}>
      <Button
        icon={<LeftOutlined />}
        onClick={onPrevious}
        disabled={!canGoPrevious}
        size="large"
      >
        Anterior
      </Button>

      {showSaveDraft && (
        <Button
          icon={<SaveOutlined />}
          onClick={onSaveDraft}
          loading={savingDraft}
          disabled={savingDraft}
          size="large"
        >
          Salvar Rascunho
        </Button>
      )}

      {isReviewMode ? (
        // Review mode: show "Próximo" on non-last steps, "Finalizar Revisão" only on last step
        isLastStep ? (
          <Button
            buttonColor="primary"
            icon={<CheckOutlined />}
            onClick={onFinish}
            size="large"
          >
            Finalizar Revisão
          </Button>
        ) : (
          <Button
            buttonColor="primary"
            icon={<RightOutlined />}
            iconPosition="end"
            onClick={onNext}
            disabled={!canGoNext}
            size="large"
          >
            Próximo
          </Button>
        )
      ) : !isLastStep ? (
        <Button
          buttonColor="primary"
          icon={<RightOutlined />}
          iconPosition="end"
          onClick={onNext}
          disabled={!canGoNext}
          size="large"
        >
          Próximo
        </Button>
      ) : (
        <div style={{ display: 'flex', gap: 12 }}>
          {processedLaudo && !processingUrls ? (
            <BlobProvider document={<LaudoDocument data={processedLaudo} logoBase64={logoBase64} isAutomatic={isEditAutomaticMode} streetViewImageBase64={streetViewImageBase64} />}>
              {({ url, loading }) => (
                <Button
                  icon={<EyeOutlined />}
                  loading={loading}
                  size="large"
                  onClick={() => url && window.open(url, '_blank')}
                  disabled={!url}
                >
                  {loading ? 'Preparando...' : 'Visualizar Laudo'}
                </Button>
              )}
            </BlobProvider>
          ) : null}
          <Button
            buttonColor="primary"
            onClick={onFinish}
            size="large"
          >
            {getFinishButtonText()}
          </Button>
        </div>
      )}
    </div>
  );
}
